import PropTypes from "prop-types";
import { useState, useMemo, useEffect } from "react";
import {
	Box,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	Button,
	Checkbox,
	TextField,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TablePagination,
	Divider,
	Accordion,
	AccordionSummary,
	Popover,
	Switch,
	FormControlLabel,
	IconButton,
	AccordionDetails,
	Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorIcon from "@mui/icons-material/Error";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
	checkMidtermEmails,
	sendMidtermEmailsToSelected,
	sendTestMidtermEmail,
	populateMidtermEmails,
} from "../../../components/Api/Getters";

function CustomTabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	);
}
CustomTabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
};

export default function MidtermEmail(props) {
	const { value, token, terms, termValue, termNumber, handleChangeTerm } = props;

	const [midtermEmailInstructors, setMidtermEmailInstructor] = useState([]);
	const [loadingMidterm, setLoadingMidterm] = useState(false);
	const [selectedCcids, setSelectedCcids] = useState(new Set());
	const [anchorEl, setAnchorEl] = useState(null);
	const openPopover = Boolean(anchorEl);

	// Filtering & pagination state
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);

	// Email configuration state
	const [emailConfig, setEmailConfig] = useState({
		termString: "Winter 2026",
		startDate: "February 9, 2026",
		endDate: "February 13, 2026",
		deadline: "February 7, 2026",
		absoluteDeadline: "March 10, 2026",
	});

	const handleEmailConfigChange = (field) => (e) => {
		setEmailConfig((prev) => ({ ...prev, [field]: e.target.value }));
	};

	// Derive status from invitation_status field
	const getStatus = (instructor) => instructor.invitation_status || "pending";

	// Filter instructors based on search and status
	const filteredInstructors = useMemo(() => {
		return midtermEmailInstructors.filter((instructor) => {
			const matchesSearch =
				searchQuery === "" ||
				instructor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				instructor.ccid?.toLowerCase().includes(searchQuery.toLowerCase());

			const status = getStatus(instructor);
			const matchesStatus =
				statusFilter === "all" ||
				(statusFilter === "pending" && status === "pending") ||
				(statusFilter === "sent" && status === "sent") ||
				(statusFilter === "failed" && status === "failed");

			return matchesSearch && matchesStatus;
		});
	}, [midtermEmailInstructors, searchQuery, statusFilter]);

	// Paginated instructors
	const paginatedInstructors = useMemo(() => {
		const start = page * rowsPerPage;
		return filteredInstructors.slice(start, start + rowsPerPage);
	}, [filteredInstructors, page, rowsPerPage]);

	// Get unsent instructors from filtered list
	const unsentInstructors = useMemo(() => {
		return filteredInstructors.filter((i) => getStatus(i) === "pending");
	}, [filteredInstructors]);

	// Check if all unsent in filtered list are selected
	const allUnsentSelected =
		unsentInstructors.length > 0 && unsentInstructors.every((i) => selectedCcids.has(i.ccid));

	useEffect(() => {
		if (termNumber) {
			handleCheckMidtermEmails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [termNumber]);

	const handleToggleSelect = (ccid) => {
		setSelectedCcids((prev) => {
			const next = new Set(prev);
			if (next.has(ccid)) {
				next.delete(ccid);
			} else {
				next.add(ccid);
			}
			return next;
		});
	};

	const handleSelectAllUnsent = () => {
		if (allUnsentSelected) {
			// Deselect all unsent
			setSelectedCcids((prev) => {
				const next = new Set(prev);
				unsentInstructors.forEach((i) => next.delete(i.ccid));
				return next;
			});
		} else {
			// Select all unsent
			setSelectedCcids((prev) => {
				const next = new Set(prev);
				unsentInstructors.forEach((i) => next.add(i.ccid));
				return next;
			});
		}
	};

	const handleClearSelection = () => {
		setSelectedCcids(new Set());
	};

	const handlePopulateMidtermEmails = async () => {
		if (!termNumber) {
			alert("Please select a term first.");
			return;
		}
		if (
			window.confirm("Are you sure you want to populate midterm emails? This may take some time.")
		) {
			try {
				const response = await populateMidtermEmails(token, termNumber, false);
				console.log("Midterm emails populated:", response);
				alert(
					"Midterm emails populated successfully. Number: " +
						response.count +
						". Existing: " +
						response.existing_count,
				);
				handleCheckMidtermEmails();
			} catch (error) {
				console.error(error);
			}
		}
	};

	const handlePopulateTestEmails = async () => {
		if (!termNumber) {
			alert("Please select a term first.");
			return;
		}
		try {
			const response = await populateMidtermEmails(token, termNumber, true);
			console.log("Midterm emails populated:", response);
			alert(
				"Midterm emails populated successfully. Number: " +
					response.count +
					". Existing: " +
					response.existing_count,
			);
			handleCheckMidtermEmails();
		} catch (error) {
			console.error(error);
		}
	};

	const handleCheckMidtermEmails = async () => {
		if (!termNumber) {
			return;
		}
		try {
			const response = await checkMidtermEmails(token, termNumber);
			if (response.instructors && response.instructors.length > 0) {
				setMidtermEmailInstructor(response.instructors);
				setSelectedCcids(new Set()); // Clear selection when refreshing
				setPage(0); // Reset to first page
			} else {
				setMidtermEmailInstructor([]);
				setSelectedCcids(new Set());
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleTestSingleEmail = async () => {
		try {
			const response = await sendTestMidtermEmail(token, emailConfig);
			if (response.sent_count === 1) {
				alert("Test midterm email sent to: " + response.to);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleSendMidtermEmails = async () => {
		if (selectedCcids.size === 0) {
			alert("Please select at least one instructor to send emails to.");
			return;
		}
		if (!termNumber) {
			alert("Please select a term first.");
			return;
		}

		const ccidsToSend = Array.from(selectedCcids);

		try {
			setLoadingMidterm(true);
			const response = await sendMidtermEmailsToSelected(
				token,
				termNumber,
				ccidsToSend,
				emailConfig,
			);

			// Update local state with results
			if (response.sent_ccids) {
				setMidtermEmailInstructor((prev) =>
					prev.map((instructor) => {
						if (response.sent_ccids.includes(instructor.ccid)) {
							return { ...instructor, invitation_status: "sent" };
						}
						if (response.failed?.some((f) => f.ccid === instructor.ccid)) {
							return { ...instructor, invitation_status: "failed" };
						}
						return instructor;
					}),
				);
				// Clear sent items from selection
				setSelectedCcids((prev) => {
					const next = new Set(prev);
					response.sent_ccids.forEach((ccid) => next.delete(ccid));
					return next;
				});
			}

			alert(`Sent: ${response.count_sent || 0}, Failed: ${response.count_failed || 0}`);
		} catch (error) {
			console.error(error);
			alert("Error sending emails. Check console for details.");
		} finally {
			setLoadingMidterm(false);
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<CustomTabPanel value={value} index={4}>
			{/* Term Selector and Email Configuration */}
			<div className="mb-6">
				<div className="flex  items-end justify-between mb-4">
					<div className="flex gap-4">
						<FormControl sx={{ minWidth: 200 }}>
							<InputLabel id="term-select-label">Term</InputLabel>
							<Select
								labelId="term-select-label"
								value={termValue}
								label="Term"
								onChange={handleChangeTerm}
							>
								{terms.map((term) => (
									<MenuItem key={term} value={term}>
										{term}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Button
							variant="contained"
							color="primary"
							onClick={handleCheckMidtermEmails}
							startIcon={<RefreshIcon />}
							disabled={!termNumber}
						>
							Check Midterm Emails
						</Button>
					</div>
					<div className="flex justify-end ">
						<div className="  flex items-center justify-end ">
							<IconButton
								size="small"
								onClick={(e) => setAnchorEl(e.currentTarget)}
								sx={{ mr: 1 }}
								aria-label="Answer key options"
							>
								<SettingsOutlinedIcon />
							</IconButton>

							<Popover
								open={openPopover}
								anchorEl={anchorEl}
								onClose={() => setAnchorEl(null)}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
								transformOrigin={{ vertical: "top", horizontal: "right" }}
								slotProps={{ paper: { sx: { p: 2, minWidth: 260 } } }}
							>
								<div className="flex  flex-col gap-2">
									<Button
										variant="outlined"
										color="primary"
										onClick={handlePopulateTestEmails}
										startIcon={<RefreshIcon />}
										disabled={!termNumber}
									>
										Populate Test Emails
									</Button>
									<Button
										variant="contained"
										color="primary"
										onClick={handlePopulateMidtermEmails}
										startIcon={<RefreshIcon />}
										disabled={!termNumber}
									>
										Populate Midterm Emails
									</Button>
								</div>
							</Popover>
						</div>
					</div>
				</div>

				<Accordion defaultExpanded={false} sx={{ mb: 2 }}>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography fontWeight={500}>Email Configuration</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
							<TextField
								label="Term String (e.g., Winter 2026)"
								value={emailConfig.termString}
								onChange={handleEmailConfigChange("termString")}
								size="small"
								fullWidth
							/>
							<TextField
								label="Survey Start Date"
								value={emailConfig.startDate}
								onChange={handleEmailConfigChange("startDate")}
								size="small"
								fullWidth
								helperText="e.g., February 9, 2026"
							/>
							<TextField
								label="Survey End Date"
								value={emailConfig.endDate}
								onChange={handleEmailConfigChange("endDate")}
								size="small"
								fullWidth
								helperText="e.g., February 13, 2026"
							/>
							<TextField
								label="Submission Deadline"
								value={emailConfig.deadline}
								onChange={handleEmailConfigChange("deadline")}
								size="small"
								fullWidth
								helperText="e.g., February 7, 2026"
							/>
							<TextField
								label="Absolute Deadline"
								value={emailConfig.absoluteDeadline}
								onChange={handleEmailConfigChange("absoluteDeadline")}
								size="small"
								fullWidth
								helperText="e.g., March 10, 2026"
							/>
						</div>
						<Divider sx={{ my: 2 }} />
						<div className="flex gap-2">
							<Button
								variant="outlined"
								color="primary"
								onClick={handleTestSingleEmail}
								startIcon={<SendIcon />}
								size="small"
							>
								Send Test Email (to yourself)
							</Button>
						</div>
					</AccordionDetails>
				</Accordion>
			</div>

			<div className="rounded border shadow  mt-6">
				{/* Filters and Stats */}
				{midtermEmailInstructors.length > 0 && (
					<div className="p-4">
						<div className="flex gap-2 items-center">
							<TextField
								size="small"
								placeholder="Search by name or ccid..."
								value={searchQuery}
								onChange={(e) => {
									setSearchQuery(e.target.value);
									setPage(0);
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{ minWidth: 250 }}
							/>
							<FormControl size="small" sx={{ minWidth: 150 }}>
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									label="Status"
									onChange={(e) => {
										setStatusFilter(e.target.value);
										setPage(0);
									}}
								>
									<MenuItem value="all">All</MenuItem>
									<MenuItem value="pending">Pending</MenuItem>
									<MenuItem value="sent">Sent</MenuItem>
									<MenuItem value="failed">Failed</MenuItem>
								</Select>
							</FormControl>
							<Button
								variant="outlined"
								size="small"
								onClick={handleSelectAllUnsent}
								disabled={unsentInstructors.length === 0}
							>
								{allUnsentSelected
									? "Deselect All Pending"
									: `Select All Pending (${unsentInstructors.length})`}
							</Button>
							{selectedCcids.size > 0 && (
								<Button variant="text" size="small" onClick={handleClearSelection}>
									Clear Selection
								</Button>
							)}
						</div>

						<div className="mt-1 ml-1 text-sm text-gray-600">
							Showing {filteredInstructors.length} of {midtermEmailInstructors.length} instructors
							{selectedCcids.size > 0 && ` • ${selectedCcids.size} selected`}
						</div>
					</div>
				)}

				{midtermEmailInstructors.length > 0 && (
					<div className="w-full shadow rounded max-h-[40dvh] overflow-y-scroll">
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow sx={{ backgroundColor: "grey.100" }}>
										<TableCell padding="checkbox">
											<Checkbox
												indeterminate={
													selectedCcids.size > 0 && selectedCcids.size < unsentInstructors.length
												}
												checked={allUnsentSelected}
												onChange={handleSelectAllUnsent}
												disabled={unsentInstructors.length === 0}
											/>
										</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
										<TableCell sx={{ fontWeight: 600 }} align="center">
											Status
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paginatedInstructors.map((instructor) => {
										const status = getStatus(instructor);
										const isSelected = selectedCcids.has(instructor.ccid);
										const isPending = status === "pending";

										return (
											<TableRow
												key={instructor.empid}
												hover
												selected={isSelected}
												sx={{ cursor: isPending ? "pointer" : "default" }}
												onClick={() => isPending && handleToggleSelect(instructor.ccid)}
											>
												<TableCell padding="checkbox">
													<Checkbox
														checked={isSelected}
														disabled={!isPending}
														onChange={() => handleToggleSelect(instructor.ccid)}
														onClick={(e) => e.stopPropagation()}
													/>
												</TableCell>
												<TableCell>{instructor.name}</TableCell>
												<TableCell>{instructor.ccid}@ualberta.ca</TableCell>
												<TableCell align="center">
													{status === "sent" && (
														<Chip
															icon={<CheckCircleIcon />}
															label="Sent"
															color="success"
															size="small"
															variant="outlined"
														/>
													)}
													{status === "pending" && (
														<Chip
															icon={<PendingIcon />}
															label="Pending"
															color="warning"
															size="small"
															variant="outlined"
														/>
													)}
													{status === "failed" && (
														<Chip
															icon={<ErrorIcon />}
															label="Failed"
															color="error"
															size="small"
															variant="outlined"
														/>
													)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							count={filteredInstructors.length}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50, 100]}
						/>
					</div>
				)}
				{midtermEmailInstructors.length > 0 && (
					<div className="p-2 flex justify-end ">
						<Button
							variant="contained"
							color="success"
							onClick={handleSendMidtermEmails}
							disabled={selectedCcids.size === 0 || loadingMidterm}
							startIcon={
								loadingMidterm ? <CircularProgress size={20} color="inherit" /> : <SendIcon />
							}
						>
							{loadingMidterm ? "Sending..." : `Send to Selected (${selectedCcids.size})`}
						</Button>
					</div>
				)}
			</div>
		</CustomTabPanel>
	);
}
